import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-DYNQVVN4K5';

const getPagePath = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return `${window.location.pathname}${window.location.search}`;
};

const getLinkLabel = (link) => {
  const text = link.textContent ? link.textContent.replace(/\s+/g, ' ').trim() : '';
  return text || link.getAttribute('aria-label') || link.href;
};

// Google Analytics Component
export const GoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Load Google Analytics script
    if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
      // Check if gtag is already loaded to avoid duplicate loading
      if (typeof window.gtag === 'undefined') {
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script1);

        const script2 = document.createElement('script');
        script2.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          window.gtag = function(){window.dataLayer.push(arguments);}
          window.gtag('js', new Date());
          window.gtag('config', '${GA_MEASUREMENT_ID}', {
            page_path: window.location.pathname,
            send_page_view: false
          });
        `;
        document.head.appendChild(script2);
      }
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (typeof window.gtag !== 'undefined' && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  useEffect(() => {
    const handleTrackedLinkClick = (event) => {
      const link = event.target.closest('a[href]');

      if (!link) {
        return;
      }

      const href = link.getAttribute('href') || '';
      const linkUrl = link.href || href;
      const linkLabel = getLinkLabel(link);
      const baseParams = {
        link_url: linkUrl,
        link_text: linkLabel,
        page_path: getPagePath(),
      };

      if (href.startsWith('tel:')) {
        trackLeadEvent('phone_click', {
          ...baseParams,
          contact_method: 'phone',
          phone_number: href.replace('tel:', ''),
        });
        return;
      }

      if (href.startsWith('mailto:')) {
        trackLeadEvent('email_click', {
          ...baseParams,
          contact_method: 'email',
          email_address: href.replace('mailto:', '').split('?')[0],
        });
        return;
      }

      if (href.includes('wa.me') || href.includes('whatsapp')) {
        trackLeadEvent('whatsapp_click', {
          ...baseParams,
          contact_method: 'whatsapp',
        });
        return;
      }

      if (href.toLowerCase().includes('.pdf') || link.hasAttribute('download')) {
        trackEvent('brochure_download', {
          ...baseParams,
          file_name: link.getAttribute('download') || href.split('/').pop(),
        });
      }
    };

    document.addEventListener('click', handleTrackedLinkClick);
    return () => document.removeEventListener('click', handleTrackedLinkClick);
  }, []);

  return null;
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      page_path: getPagePath(),
      ...eventParams,
    });
  }
};

export const trackLeadEvent = (eventName, eventParams = {}) => {
  trackEvent(eventName, {
    event_category: 'lead',
    ...eventParams,
  });
};

// Track conversions
export const trackConversion = (conversionName, value = 0) => {
  trackLeadEvent(conversionName, {
    value: value,
    currency: 'USD',
  });
};

// Track form submissions
export const trackFormSubmission = (formName, formParams = {}) => {
  trackLeadEvent('form_submission', {
    form_name: formName,
    ...formParams,
  });
};

// Track button clicks
export const trackButtonClick = (buttonName, buttonLocation) => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
  });
};
